/* =============================================================
   IWearStyle™ — Global JavaScript
   File: assets/iws-global.js
   ============================================================= */

(function () {
  'use strict';

  /* ── SCROLL REVEAL ─────────────────────────────────────────
     Uses IntersectionObserver to trigger .iws-reveal elements.
     Elements get .is-visible when they enter the viewport.
  ─────────────────────────────────────────────────────────── */
  function initReveal() {
    if (!('IntersectionObserver' in window)) {
      // Fallback: show all immediately
      document.querySelectorAll('.iws-reveal').forEach(el => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    document.querySelectorAll('.iws-reveal').forEach(el => observer.observe(el));
  }

  /* ── HEADER SCROLL SHADOW ──────────────────────────────────*/
  function initHeaderScroll() {
    const header = document.getElementById('iws-header');
    if (!header) return;
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ── FILTER TABS ───────────────────────────────────────────
     Simple tab switching for product grid filters.
     Extend with AJAX collection filtering if needed.
  ─────────────────────────────────────────────────────────── */
  function initFilterTabs() {
    document.querySelectorAll('.iws-filter-tabs').forEach(tabGroup => {
      tabGroup.querySelectorAll('.iws-filter-tab').forEach(tab => {
        tab.addEventListener('click', () => {
          tabGroup.querySelectorAll('.iws-filter-tab').forEach(t => t.classList.remove('is-active'));
          tab.classList.add('is-active');
          // Dispatch custom event for extensions to hook into
          tabGroup.dispatchEvent(new CustomEvent('iws:filter-change', {
            bubbles: true,
            detail: { value: tab.dataset.filter }
          }));
        });
      });
    });
  }

  /* ── CART NOTIFICATIONS ────────────────────────────────────
     Shows a subtle toast when item added to cart.
  ─────────────────────────────────────────────────────────── */
  function showCartToast(productName) {
    let toast = document.getElementById('iws-cart-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'iws-cart-toast';
      toast.style.cssText = `
        position: fixed;
        bottom: 24px;
        right: 24px;
        background: var(--ink);
        color: white;
        padding: 14px 20px;
        border-radius: 4px;
        font-family: var(--sans);
        font-size: 13px;
        font-weight: 400;
        letter-spacing: 0.04em;
        box-shadow: 0 8px 30px rgba(0,0,0,0.2);
        z-index: 9999;
        transform: translateY(80px);
        opacity: 0;
        transition: transform 0.3s cubic-bezier(0.22,1,0.36,1), opacity 0.3s;
        max-width: 280px;
      `;
      document.body.appendChild(toast);
    }
    toast.textContent = `✓ ${productName || 'Item'} added to cart`;
    // Show
    requestAnimationFrame(() => {
      toast.style.transform = 'translateY(0)';
      toast.style.opacity = '1';
    });
    // Hide after 2.5s
    clearTimeout(toast._timeout);
    toast._timeout = setTimeout(() => {
      toast.style.transform = 'translateY(80px)';
      toast.style.opacity = '0';
    }, 2500);
  }

  // Expose globally so quick-add buttons can use it
  window.iwsShowCartToast = showCartToast;

  /* ── WISHLIST (localStorage) ───────────────────────────────
     Saves wished product IDs to localStorage.
     Visually marks saved items with filled heart.
  ─────────────────────────────────────────────────────────── */
  function initWishlist() {
    const KEY = 'iws_wishlist';
    const getList = () => JSON.parse(localStorage.getItem(KEY) || '[]');
    const saveList = (list) => localStorage.setItem(KEY, JSON.stringify(list));

    function toggleWish(btn) {
      const id = btn.closest('[data-product-id]')?.dataset?.productId;
      if (!id) return;
      const list = getList();
      const idx = list.indexOf(id);
      if (idx === -1) {
        list.push(id);
        btn.querySelector('svg').style.fill = 'currentColor';
        btn.querySelector('svg').style.color = '#D4524A';
      } else {
        list.splice(idx, 1);
        btn.querySelector('svg').style.fill = 'none';
        btn.querySelector('svg').style.color = 'var(--ink)';
      }
      saveList(list);
    }

    // Restore state on page load
    const list = getList();
    document.querySelectorAll('.iws-product-wishlist').forEach(btn => {
      const id = btn.closest('[data-product-id]')?.dataset?.productId;
      if (id && list.includes(id)) {
        btn.querySelector('svg').style.fill = 'currentColor';
        btn.querySelector('svg').style.color = '#D4524A';
      }
      btn.addEventListener('click', () => toggleWish(btn));
    });
  }

  /* ── SMOOTH ANCHOR SCROLL ──────────────────────────────────*/
  function initSmoothAnchors() {
    document.querySelectorAll('a[href^="#"]').forEach(a => {
      a.addEventListener('click', e => {
        const target = document.querySelector(a.getAttribute('href'));
        if (target) {
          e.preventDefault();
          const headerH = document.getElementById('iws-header')?.offsetHeight || 0;
          const y = target.getBoundingClientRect().top + window.scrollY - headerH - 16;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      });
    });
  }

  /* ── INIT ─────────────────────────────────────────────────*/
  document.addEventListener('DOMContentLoaded', () => {
    initReveal();
    initHeaderScroll();
    initFilterTabs();
    initWishlist();
    initSmoothAnchors();
  });

  // Re-run reveal after Shopify section changes in theme editor
  document.addEventListener('shopify:section:load', () => {
    initReveal();
  });

})();
